import { forwardRef, useState } from "react";
import { CAMPAIGN } from "@/lib/campaign";
import { scrollToId } from "@/lib/scroll";
import { REPLAY, embedUrlFor } from "@/lib/replay";
import { PREVIEW_MODE } from "@/lib/campaign";
import { SectionBg } from "@/components/SectionBg";
import { sendReplayEvent, startMilestoneTimer } from "@/lib/replayTracking";

const VideoPlayer = () => {
  const [playing, setPlaying] = useState(false);
  const embedSrc = embedUrlFor(REPLAY.videoUrl);

  const onPlay = () => {
    setPlaying(true);
    startMilestoneTimer(REPLAY.durationSeconds);
  };

  return (
    <div className="glass-panel rounded-[6px] overflow-hidden" data-testid="replay-player-card">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--line)]">
        <p className="font-mono text-[10px] tracking-[0.24em] text-[var(--accent)]">FULL WORKSHOP REPLAY</p>
        {REPLAY.durationLabel && (
          <p className="font-mono text-[10px] tracking-[0.12em] text-[var(--muted)]">{REPLAY.durationLabel}</p>
        )}
      </div>
      <div className="relative w-full" style={{ aspectRatio: "16 / 9", background: "var(--background-deep)" }}>
        {playing && embedSrc ? (
          <iframe
            src={embedSrc}
            title="Benefit to Keys — Full Workshop Replay"
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : embedSrc ? (
          <button
            data-testid="replay-play-button"
            onClick={onPlay}
            aria-label="Play the full workshop replay"
            className="absolute inset-0 w-full h-full flex items-center justify-center group"
            style={REPLAY.posterUrl ? { backgroundImage: `url(${REPLAY.posterUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          >
            <span className="w-20 h-20 rounded-full bg-[var(--accent)] text-[var(--background-deep)] flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300">
              ▶
            </span>
          </button>
        ) : (
          PREVIEW_MODE && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="replay-pending-placeholder">
              <span aria-hidden="true" className="w-16 h-16 rounded-full border border-[var(--accent)] text-[var(--accent)] flex items-center justify-center text-xl opacity-60">▶</span>
              <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)]">
                REPLAY VIDEO PENDING FINAL UPLOAD
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

/* REPLAY SECTION 1 — HERO / PLAYER */
export const ReplayHero = forwardRef((props, heroRef) => (
  <section id="watch" ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16 px-5 md:px-10 lg:px-16 overflow-hidden">
    <SectionBg img="bg-home-4.jpg" strength={0.91} />
    <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 lg:gap-14 items-center">
      {/* video first on mobile */}
      <div className="relative order-1 lg:order-2">
        <div className="relative z-10">
          <VideoPlayer />
          <button
            data-testid="replay-hero-cta"
            onClick={() => {
              sendReplayEvent("cta_clicked");
              scrollToId("book");
            }}
            className="glint w-full mt-4 bg-[var(--accent)] text-[var(--background-deep)] font-semibold text-sm tracking-[0.1em] py-4 rounded-[4px] hover:brightness-110 transition-[filter] duration-200"
          >
            REQUEST MY COMPLIMENTARY BENEFIT-TO-KEYS SESSION
          </button>
          <p className="font-mono text-[10px] tracking-[0.06em] text-[var(--muted)] mt-3 text-center">
            No fee for the initial educational session · No obligation to apply
          </p>
        </div>
      </div>

      <div className="text-left order-2 lg:order-1">
        <p className="mask-line font-mono text-[11px] md:text-xs tracking-[0.3em] text-[var(--accent)] mb-8">
          <span style={{ animationDelay: "0.05s" }}>BENEFIT TO KEYS · FLORIDA VA HOMEBUYING REPLAY</span>
        </p>
        <h1 className="mb-8">
          <span className="mask-line font-display font-semibold text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] text-white">
            <span style={{ animationDelay: "0.2s" }}>UNDERSTAND THE BENEFIT.</span>
          </span>
          <span className="mask-line font-display font-semibold text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.06] text-[var(--accent)]">
            <span style={{ animationDelay: "0.3s" }}>THEN MAP YOUR NEXT MOVE.</span>
          </span>
          <span className="mask-line font-mono font-normal tracking-[0.14em] text-base sm:text-lg lg:text-[2.1rem] leading-snug text-white/60 mt-4">
            <span style={{ animationDelay: "0.4s" }}>VERIFY · MODEL · MOVE</span>
          </span>
        </h1>
        <p className="text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.8)] max-w-xl mb-7">
          Watch the Benefit-to-Keys workshop, then request a private educational session to organize the
          eligibility, entitlement, buying-power, cash, credit, and purchase-path questions that may be
          relevant to a Florida home purchase.
        </p>
        <blockquote className="font-display italic text-lg md:text-xl leading-relaxed text-[rgba(255,255,255,0.72)] border-l-2 border-[var(--accent)] pl-5 max-w-xl mb-9">
          The benefit deserves a complete plan—not a headline, myth, or guess.
        </blockquote>
        <div className="flex items-center gap-4" data-testid="replay-host-row">
          <img
            src={CAMPAIGN.headshot}
            alt={`${CAMPAIGN.hostName}, ${CAMPAIGN.hostCompany}`}
            className="w-12 h-12 rounded-[6px] object-cover object-top border border-[var(--line)]"
          />
          <div className="text-left">
            <p className="text-sm font-medium text-white">{CAMPAIGN.hostName}</p>
            <p className="font-mono text-[10px] tracking-[0.1em] text-[var(--muted)]">
              {CAMPAIGN.hostCompany} · Individual NMLS #{CAMPAIGN.hostNmls}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
));
